import { useEffect, useMemo, useState } from 'react';
import type {
  CtaBlockPayload,
  FaqBlockPayload,
  HeroBlockPayload,
  PlanDefinition,
  PlanTier,
  PricingTableBlockPayload,
} from '@opinix-cloud/shared';
import { fetchPlans } from '../lib/plans';
import { PLANS_FALLBACK } from '../lib/plans-fallback';
import { PlanCard } from '../components/PlanCard';
import { PlanEarlyAccessModal } from '../components/PlanEarlyAccessModal';
import { FeatureMatrix } from '../components/FeatureMatrix';
import { PricingFAQ } from '../components/PricingFAQ';
import { EarlyAccessForm } from '../components/EarlyAccessForm';
import { getBlockPayload } from '../lib/content';
import { useContentSurface } from '../lib/useContentSurface';

export type BillingCycle = 'monthly' | 'annual';

interface PricingPageProps {
  onRequestDemo: (notes?: string) => void;
}

interface EarlyAccessTarget {
  plan: PlanTier;
  planName: string;
}

export function PricingPage({ onRequestDemo }: PricingPageProps): JSX.Element {
  const [plans, setPlans] = useState<PlanDefinition[]>(PLANS_FALLBACK);
  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const [eaTarget, setEaTarget] = useState<EarlyAccessTarget | null>(null);
  const blocks = useContentSurface('pricing_page');

  useEffect(() => {
    let cancelled = false;
    fetchPlans().then((p) => {
      if (!cancelled) setPlans(p);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const orderedPlans = useMemo(() => orderPlans(plans), [plans]);

  function handleCta(plan: PlanDefinition): void {
    switch (plan.tier) {
      case 'STARTER':
      case 'PRO':
        setEaTarget({ plan: plan.tier, planName: plan.name });
        return;
      case 'ENTERPRISE':
        onRequestDemo(`Interested in Enterprise pricing`);
        return;
    }
  }

  const hero = getBlockPayload<HeroBlockPayload>(blocks, 'pricing_page', 'hero');
  const table = getBlockPayload<PricingTableBlockPayload>(
    blocks,
    'pricing_page',
    'pricing_table',
  );
  const faq = getBlockPayload<FaqBlockPayload>(blocks, 'pricing_page', 'faq');
  const finalCta = getBlockPayload<CtaBlockPayload>(
    blocks,
    'pricing_page',
    'final_cta',
  );

  return (
    <main>
      <PricingHero
        cycle={cycle}
        onCycleChange={setCycle}
        content={hero}
      />

      <section className="bg-steel-50 -mt-12 pb-24 sm:pb-32">
        <div className="container-x relative">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {orderedPlans.map((plan) => (
              <PlanCard
                key={plan.tier}
                plan={plan}
                cycle={cycle}
                featured={plan.tier === 'PRO'}
                onCta={() => handleCta(plan)}
              />
            ))}
          </div>
          {table.footnote && (
            <p className="mt-8 text-center text-sm text-steel-500">
              {table.footnote}
            </p>
          )}
        </div>
      </section>

      <FeatureMatrix plans={orderedPlans} />
      <PricingFAQ content={faq} />
      <PricingFinalCTA onRequestDemo={onRequestDemo} content={finalCta} />

      <PlanEarlyAccessModal
        open={eaTarget !== null}
        onClose={() => setEaTarget(null)}
        plan={eaTarget?.plan ?? null}
        planName={eaTarget?.planName ?? ''}
      />
    </main>
  );
}

const TIER_ORDER: PlanTier[] = ['STARTER', 'PRO', 'ENTERPRISE'];

function orderPlans(plans: PlanDefinition[]): PlanDefinition[] {
  const byTier = new Map(plans.map((p) => [p.tier, p]));
  return TIER_ORDER.map((tier) => byTier.get(tier)).filter(
    (p): p is PlanDefinition => Boolean(p),
  );
}

interface PricingHeroProps {
  cycle: BillingCycle;
  onCycleChange: (cycle: BillingCycle) => void;
  content: HeroBlockPayload;
}

function PricingHero({
  cycle,
  onCycleChange,
  content,
}: PricingHeroProps): JSX.Element {
  const headlineParts = splitHeadline(
    content.headline,
    content.headlineAccent ?? null,
  );
  return (
    <section className="relative overflow-hidden bg-navy-900 pt-32 pb-32 sm:pt-40 sm:pb-40">
      <div
        className="pointer-events-none absolute inset-0 bg-grid-faint bg-grid-32 opacity-60"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-0 bg-radial-cyan"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-0 bg-radial-ember"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-navy-950/60 to-transparent"
        aria-hidden="true"
      />

      <div className="container-x relative">
        <div className="max-w-3xl">
          {content.badge && (
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-300">
              {content.badge}
            </div>
          )}
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tightest text-white leading-[1.05]">
            {headlineParts.before}
            {headlineParts.accent && (
              <span className="text-cyan-300">{headlineParts.accent}</span>
            )}
            {headlineParts.after}
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-steel-300 max-w-2xl leading-relaxed">
            {content.subhead}
          </p>
        </div>

        <div className="mt-12 flex justify-center">
          <BillingToggle cycle={cycle} onChange={onCycleChange} />
        </div>
      </div>
    </section>
  );
}

interface BillingToggleProps {
  cycle: BillingCycle;
  onChange: (cycle: BillingCycle) => void;
}

function BillingToggle({ cycle, onChange }: BillingToggleProps): JSX.Element {
  return (
    <div
      role="tablist"
      aria-label="Billing cycle"
      className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] p-1 backdrop-blur-sm"
    >
      <ToggleButton
        active={cycle === 'monthly'}
        onClick={() => onChange('monthly')}
      >
        Monthly
      </ToggleButton>
      <ToggleButton
        active={cycle === 'annual'}
        onClick={() => onChange('annual')}
      >
        Annual
        <span className="ml-2 inline-flex items-center rounded-full bg-cyan-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-cyan-300">
          2 mo free
        </span>
      </ToggleButton>
    </div>
  );
}

interface PricingFinalCTAProps {
  onRequestDemo: (notes?: string) => void;
  content: CtaBlockPayload;
}

function PricingFinalCTA({
  onRequestDemo,
  content,
}: PricingFinalCTAProps): JSX.Element {
  return (
    <section className="bg-navy-900 py-24 sm:py-32 relative overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0 bg-grid-faint bg-grid-32 opacity-50"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-0 bg-radial-cyan"
        aria-hidden="true"
      />
      <div className="container-x relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div>
            {content.eyebrow && (
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">
                {content.eyebrow}
              </p>
            )}
            <h2 className="mt-3 section-title-light">{content.heading}</h2>
            {content.blurb && (
              <p className="mt-5 text-lg text-steel-300 leading-relaxed">
                {content.blurb}
              </p>
            )}
            <div className="mt-8">
              <button
                type="button"
                onClick={() => onRequestDemo()}
                className="btn btn-primary"
              >
                {content.primaryCtaLabel ?? 'Request a demo'}
                <svg
                  aria-hidden="true"
                  className="ml-2 h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M4 10h12m-4-4 4 4-4 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {content.primaryCtaFootnote && (
                <p className="mt-3 text-sm text-steel-400">
                  {content.primaryCtaFootnote}
                </p>
              )}
            </div>
          </div>
          {content.secondaryCard && (
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-sm">
              <h3 className="text-lg font-bold text-white tracking-tight">
                {content.secondaryCard.heading}
              </h3>
              {content.secondaryCard.blurb && (
                <p className="mt-2 text-sm text-steel-300">
                  {content.secondaryCard.blurb}
                </p>
              )}
              <div className="mt-6 rounded-lg bg-white p-5">
                <EarlyAccessForm />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function ToggleButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`inline-flex items-center rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
        active
          ? 'bg-white text-navy-900 shadow'
          : 'text-steel-300 hover:text-white'
      }`}
    >
      {children}
    </button>
  );
}

function splitHeadline(
  headline: string,
  accent: string | null,
): { before: string; accent: string | null; after: string } {
  if (!accent) return { before: headline, accent: null, after: '' };
  const idx = headline.indexOf(accent);
  if (idx < 0) return { before: headline, accent: null, after: '' };
  return {
    before: headline.slice(0, idx),
    accent,
    after: headline.slice(idx + accent.length),
  };
}
